import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestCasinoNextInstances,
  requestGameBoxRaffleTickets,
  requestGameBoxMission,
  successGameBoxRaffle,
  updateGameBoxMission,
} from '../store/actionCreators/gameBoxMission';
import { racePointsUpdated, requestReadMessage } from '../store/actionCreators/messages';
import { successMission, updateMission, updateMissionTotalWinAndFreeSpinCount } from '../store/actionCreators/mission';
import {
  requestProgressiveMissions,
  updateProgressiveMissions,
  updateProgressiveMissionsChain,
} from '../store/actionCreators/progressiveMissions';
import { transformRaffleData } from '../store/sagas/helpers/transform';
import { getSelectedMissionId, getSplashMessages } from '../store/selectors';
import { useFormatCurrency } from './useFormatCurrency';
import { requestRaces } from "../store/actionCreators/races";

const useMessagesHandler = () => {
  const dispatch = useDispatch();
  const formatCurrency = useFormatCurrency();
  const arrMessages = useSelector(getSplashMessages);
  const selectedMissionId = useSelector(getSelectedMissionId);

  // Add ref to track processed template actions
  const processingLock = useRef(new Map());

  useEffect(() => {
    const templatedMsg = arrMessages.find((msg: { templateId: string | number }) => !!msg.templateId);
    if (!!templatedMsg) {
      // Create a unique key for this message action
      const messageKey = `${templatedMsg.templateId}-${JSON.stringify(templatedMsg.data)}`;

      // Skip if this exact message is already being processed
      if (processingLock.current.has(messageKey)) {
        return;
      }

      // Mark as being processed
      processingLock.current.set(messageKey, true);

      let progressiveMissions = null;
      switch (templatedMsg.templateId) {
        case 'newWageringMissionUpdateNotification':
          if (templatedMsg.data.length) {
            dispatch(successMission(templatedMsg.data));
          }
          break;
        case 'newFreespinNotification':
          dispatch(updateMissionTotalWinAndFreeSpinCount(templatedMsg.data, selectedMissionId));
          break;
        case 'gameBoxMissionsUpdate':
          progressiveMissions = templatedMsg?.data?.length
              ? templatedMsg?.data?.find((el: { isProgressive: any }) => el.isProgressive)
              : templatedMsg?.data?.isProgressive;
          dispatch(updateGameBoxMission(templatedMsg.data));
          const progressiveMissionsData = templatedMsg?.data?.length
              ? templatedMsg?.data?.filter((el: { isProgressive: any }) => el.isProgressive)
              : templatedMsg.data.mission;
          if (!!progressiveMissions) {
            templatedMsg?.data?.length && dispatch(updateProgressiveMissions(progressiveMissionsData));
          }
          break;
        case 'rafflePrizeChanged':
          dispatch(requestGameBoxRaffleTickets());
          break;
        case 'gameboxPrize':
          localStorage.removeItem('buttonShown');
          localStorage.setItem('missionCompleted', 'true');
          const toasterTextParams: any = {};
          if (['progressiveMission', 'mission'].includes(templatedMsg?.data?.mission?.prize?.type)) {
            dispatch(requestReadMessage(templatedMsg._id));
          } else if (templatedMsg.data?.isProgressive) {
            break;
          } else if (!!templatedMsg?.data?.progressiveMissionChain) {
            const { progressiveMissionChain, progressiveMission, currentIndex } = templatedMsg?.data;
            const validTemplates = progressiveMissionChain?.filter((el: { template_id: string | number }) => el.template_id);
            dispatch(updateProgressiveMissionsChain(validTemplates, currentIndex));
            if (progressiveMission?.current_index === validTemplates.length) {
              dispatch(requestProgressiveMissions());
            }
          } else {
            switch (templatedMsg?.data?.mission?.prize?.type) {
              case 'freespins':
                toasterTextParams.prize = templatedMsg?.data?.mission?.prize?.spinCount;
                break;
              case 'tourTicket':
                toasterTextParams.missionName = templatedMsg.data?.name;
                dispatch(updateGameBoxMission([templatedMsg?.data?.mission]));
                dispatch(
                    requestCasinoNextInstances({
                      tourInstanceId: templatedMsg?.data?.mission?.prize.tourInstanceId,
                      templateId: templatedMsg?.data?.mission?.prize.templateId,
                    })
                );
                break;
              case 'money':
                toasterTextParams.prize =
                    templatedMsg.data.mission?.prize?.currency === 'FPP'
                        ? templatedMsg.data.mission?.prize?.amount + ' FPP'
                        : formatCurrency(
                            templatedMsg.data.mission?.prize?.amount,
                            templatedMsg.data.mission?.prize?.currency
                        );
                break;
              case 'walletsTransfer':
                break;
              case 'raffleTicket':
                const hideMessages = sessionStorage?.getItem('hideMessages');
                const transformData = transformRaffleData(templatedMsg.data.raffle);
                dispatch(successGameBoxRaffle(transformData));
                if (hideMessages) return;
                break;
              default:
            }
          }
          break;
        case 'newMission':
        case 'raceFinishAndPrize':
          // Only process if not already processing this specific message
          if (!processingLock.current.has(`${templatedMsg.templateId}-processing`)) {
            processingLock.current.set(`${templatedMsg.templateId}-processing`, true);

            dispatch(requestProgressiveMissions());
            dispatch(requestGameBoxMission());
            dispatch(requestRaces());

            // Clear the processing lock after a short delay
            setTimeout(() => {
              processingLock.current.delete(`${templatedMsg.templateId}-processing`);
            }, 1000);
          }
          break;
        case 'wagerFreespinsComplete':
          dispatch(updateMission('balance', templatedMsg.data.balance, selectedMissionId));
          dispatch(requestGameBoxMission());
          break;
        case 'funMissionFail':
          dispatch(requestGameBoxMission());
          break;
        case "racePointsUpdated":
          dispatch(racePointsUpdated(templatedMsg.data));
          break;
        case "newFreespinMission":
          if (!processingLock.current.has('newFreespinMission-processing')) {
            processingLock.current.set('newFreespinMission-processing', true);

            dispatch(requestProgressiveMissions());
            dispatch(requestGameBoxMission());

            setTimeout(() => {
              processingLock.current.delete('newFreespinMission-processing');
            }, 1000);
          }
          break;
        default:
          dispatch(requestReadMessage(templatedMsg._id));
          break;
      }

      // Clean up the processing lock for this specific message after processing
      // Use setTimeout to ensure the lock is cleared after current execution cycle
      setTimeout(() => {
        processingLock.current.delete(messageKey);
      }, 0);
    }
  }, [arrMessages, dispatch]);

};

export default useMessagesHandler;